import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { NotificationTextMessage } from '@/core/enums/notification';
import {
  BusinessType,
  type AccountTypeOption,
  type ChartTemplateAccountRow,
  type ChartTemplateQuery,
  type LegacyAccountCodeRow,
  type NamedOption,
} from '@/core/models/firm-settings.models';
import { notifyError, notifySuccess } from '@/core/notify/notify';
import { matrixPath } from '@/core/routing/matrix';
import { firmSettingsService } from '@/core/services/firmSettings';
import { ClosePopup } from '@/kit/ClosePopup';
import { ConfirmPopup } from '@/kit/ConfirmPopup';
import { openDialog } from '@/kit/dialog';

export interface ChartTemplateGrid {
  businessType: BusinessType;
  dataSource: ChartTemplateAccountRow[];
  statuses: NamedOption[];
  accountTypes: AccountTypeOption[];
  selectedIds: ReadonlySet<string>;
  /** 0 shows every category. */
  accountTypeFilter: number;
  status: number;
  /** The archive checkbox shows what the user just ticked until the next list arrives (Angular re-creates the rows). */
  archivedShown: (row: ChartTemplateAccountRow) => boolean;
  /** The search box's text, kept here so a mapping row can set it. */
  searchText: string;
  /**
   * BUG-011: old Capium codes/names the search matches and the current account each maps to (empty when the search is
   * empty or matches none). Not accounts: the grid never shows them.
   */
  legacyMatches: LegacyAccountCodeRow[];
  onSearch: (value: string) => void;
  /** Sets the search box to `value` and searches at once (a mapping row's "show current account"). */
  applySearch: (value: string) => void;
  onStatusChange: (status: number | null) => void;
  onAccountTypeChange: (accountTypeId: number | null) => void;
  onAddClick: () => void;
  onEditClick: (row: ChartTemplateAccountRow) => void;
  onExport: (format: number) => void;
  onArchive: (row: ChartTemplateAccountRow, archived: boolean) => void;
  isAllSelected: () => boolean;
  toggleAll: (checked: boolean) => void;
  toggleRow: (row: ChartTemplateAccountRow, checked: boolean) => void;
  openDeleteClick: (row: ChartTemplateAccountRow) => void;
  /** Deletes every ticked account (the selection bar), after the same confirmation. */
  deleteSelected: () => void;
}

const DEFAULT_STATUSES: NamedOption[] = [
  { id: -1, name: 'All' },
  { id: 1, name: 'Active' },
  { id: 0, name: 'Inactive' },
  { id: 2, name: 'Archived' },
];

/**
 * Angular `ChartTemplateGridBase`: shared behaviour of the four default chart-of-accounts grids (one per business
 * type). It loads on mount whether or not the grid is visible, like the eagerly created Material tab content.
 */
export function useChartTemplateGrid(businessType: BusinessType): ChartTemplateGrid {
  const navigate = useNavigate();
  const [dataSource, setDataSource] = useState<ChartTemplateAccountRow[]>([]);
  const [statuses, setStatuses] = useState<NamedOption[]>(DEFAULT_STATUSES);
  const [accountTypes, setAccountTypes] = useState<AccountTypeOption[]>([]);
  const [selectedIds, setSelectedIds] = useState<ReadonlySet<string>>(() => new Set());
  const [accountTypeFilter, setAccountTypeFilter] = useState(0);
  const [status, setStatus] = useState(-1);
  const [searchText, setSearchText] = useState('');
  const [legacyMatches, setLegacyMatches] = useState<LegacyAccountCodeRow[]>([]);
  const [archiveOverride, setArchiveOverride] = useState<Record<string, boolean>>({});

  const query = useRef<ChartTemplateQuery>({ search: null, status: -1, accountTypeId: null });
  const selected = useRef<ReadonlySet<string>>(selectedIds);
  const mounted = useRef(true);
  const searchTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const lastSearch = useRef<{ value: string } | null>(null);
  const legacyRequest = useRef(0);

  const select = (next: ReadonlySet<string>) => {
    selected.current = next;
    setSelectedIds(next);
  };

  const getList = useCallback(() => {
    firmSettingsService
      .getChartTemplate(businessType, query.current)
      .then((loaded) => {
        if (!mounted.current) return;
        // The chart is the client's finalised sheet and the API returns every account: nothing is added or rewritten here.
        // Oct-10 #173-#176: as 3.0's normal view, "All Statuses" leaves archived accounts out; the Archived status lists them.
        const list = query.current.status === -1 ? loaded.filter((row) => !row.isArchived) : loaded;

        setDataSource(list);
        setArchiveOverride({});
        const kept = new Set([...selected.current].filter((id) => list.some((row) => row.id === id)));
        selected.current = kept;
        setSelectedIds(kept);
      })
      .catch((error) => {
        if (mounted.current) notifyError(error);
      });
  }, [businessType]);

  useEffect(() => {
    mounted.current = true;
    firmSettingsService
      .getChartTemplateOptions()
      .then((options) => {
        if (!mounted.current) return;
        setStatuses(options.statuses);
        setAccountTypes(options.accountTypes);
      })
      .catch((error) => {
        if (mounted.current) notifyError(error);
      });
    getList();
    return () => {
      mounted.current = false;
      clearTimeout(searchTimer.current);
    };
  }, [getList]);

  /** BUG-011: the old Capium codes the search matches; nothing is asked when the search is empty (or "%", all accounts). */
  const loadLegacyMatches = (trimmed: string) => {
    const request = ++legacyRequest.current;
    if (!trimmed || trimmed === '%') {
      setLegacyMatches([]);
      return;
    }
    firmSettingsService
      .getLegacyAccountCodes(businessType, trimmed)
      .then((rows) => {
        if (mounted.current && request === legacyRequest.current) setLegacyMatches(rows);
      })
      .catch(() => {
        // A supplementary lookup: the grid still works without it, so a failure is not an error toast.
        if (mounted.current && request === legacyRequest.current) setLegacyMatches([]);
      });
  };

  const search = (text: string) => {
    if (lastSearch.current && lastSearch.current.value === text) return;
    lastSearch.current = { value: text };
    const trimmed = text.trim();
    // AP Parity: searching '%' returns all accounts
    query.current = { ...query.current, search: trimmed === '%' ? null : trimmed || null };
    getList();
    loadLegacyMatches(trimmed);
  };

  const onSearch = (value: string) => {
    const text = value ?? '';
    setSearchText(text);
    clearTimeout(searchTimer.current);
    // debounceTime(300) then distinctUntilChanged (on the raw text).
    searchTimer.current = setTimeout(() => search(text), 300);
  };

  const applySearch = (value: string) => {
    clearTimeout(searchTimer.current);
    setSearchText(value);
    search(value);
  };

  /** Archives (or restores) an account once the user has confirmed it (Oct-10 #173-#176). */
  function archiveAccount(row: ChartTemplateAccountRow, archived: boolean): void {
    firmSettingsService
      .archiveChartTemplateAccount(row.id, archived)
      .then(() => {
        if (!mounted.current) return;
        notifySuccess(
          archived
            ? NotificationTextMessage.recordArchivedSuccessfully
            : NotificationTextMessage.recordRestoredSuccessfully,
        );
        getList();
      })
      .catch((error) => {
        if (!mounted.current) return;
        notifyError(error);
        getList();
      });
  }

  return {
    businessType,
    dataSource,
    statuses,
    accountTypes,
    selectedIds,
    accountTypeFilter,
    status,
    archivedShown: (row) => archiveOverride[row.id] ?? row.isArchived,

    searchText,
    legacyMatches,
    onSearch,
    applySearch,

    onStatusChange: (value) => {
      const next = value ?? -1;
      setStatus(next);
      query.current = { ...query.current, status: next };
      getList();
    },

    onAccountTypeChange: (accountTypeId) => {
      setAccountTypeFilter(accountTypeId ?? 0);
      query.current = { ...query.current, accountTypeId: accountTypeId ? accountTypeId : null };
      getList();
    },

    onAddClick: () => void navigate(matrixPath('/home/settings', 'addnewaccount', { type: businessType })),

    onEditClick: (row) =>
      void navigate(matrixPath('/home/settings', 'addnewaccount', { id: btoa(row.id), type: businessType })),

    onExport: (format) => {
      firmSettingsService
        .exportChartTemplate(businessType, { ...query.current, format, ids: [...selected.current] })
        .catch((error) => notifyError(error));
    },

    onArchive: (row, archived) => {
      // Oct-10 #173-#176: as in 3.0, archiving (or restoring) an account is confirmed first; Cancel leaves it as it was.
      const dialogRef = openDialog(ConfirmPopup, {
        data: {
          title: archived ? 'Archive account' : 'Restore account',
          message: `${archived ? 'Archive' : 'Restore'} ${row.code} ${row.accountName}?`,
        },
      });
      void dialogRef.afterClosed().then((confirmed) => {
        if (!confirmed || !mounted.current) return;
        setArchiveOverride((current) => ({ ...current, [row.id]: archived }));
        archiveAccount(row, archived);
      });
    },

    isAllSelected: () => dataSource.length > 0 && dataSource.every((row) => selectedIds.has(row.id)),

    toggleAll: (checked) => select(new Set(checked ? dataSource.map((row) => row.id) : [])),

    toggleRow: (row, checked) => {
      const next = new Set(selected.current);
      if (checked) {
        next.add(row.id);
      } else {
        next.delete(row.id);
      }
      select(next);
    },

    openDeleteClick: (row) =>
      confirmDelete(
        selected.current.has(row.id) && selected.current.size > 1 ? [...selected.current] : [row.id],
        `${row.code} ${row.accountName}`,
      ),

    deleteSelected: () => confirmDelete([...selected.current]),
  };

  function confirmDelete(ids: string[], name?: string) {
    const dialogRef = openDialog(ClosePopup, {
      data: { itemName: ids.length > 1 || !name ? 'Accounts' : name, count: ids.length },
    });

    void dialogRef.afterClosed().then((confirmed) => {
      if (!confirmed) {
        return;
      }
      const handleDone = () => {
        notifySuccess(NotificationTextMessage.recordDeletedSuccessfully);
        const next = new Set(selected.current);
        ids.forEach((id) => next.delete(id));
        select(next);
        getList();
      };

      if (ids.length === 0) {
        handleDone();
        return;
      }

      firmSettingsService
        .deleteChartTemplateAccounts(ids)
        .then(handleDone)
        .catch((error) => notifyError(error));
    });
  }
}
